import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { ArrowLeft, Briefcase, Edit, ExternalLink, FileText, History, Loader2, MessageSquare, Save, Scale, Tag, Trash2, User, X } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { NativeSelect } from '@/components/ui/native-select'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { DetailField, DetailSection } from '@/components/common/DetailSection'
import { FormField } from '@/components/common/FormSection'
import { TagsInput } from '@/components/common/TagsInput'
import {
  getClientDisplayName,
  InteractionPriorityText,
  InteractionStatusBadge,
  interactionPriorityLabels as priorityLabels,
  interactionStatusLabels as statusLabels,
  interactionTypeLabels,
  type InteractionPriority,
  type InteractionStatus,
  type InteractionType
} from '@/components/interactions/InteractionBadges'
import { clientService } from '../services/client-service'
import { Client, ClientInteraction } from '../types/client'
import { useDateTimeFormat } from '../hooks/useDateTimeFormat'
import { WhatsAppLink } from '../components/common/WhatsAppLink'
import { TaskQuickCreateButton, TaskContext } from '../components/tasks/TaskQuickCreateButton'
import { TaskListByEntity } from '../components/tasks/TaskListByEntity'
import { ContextualBreadcrumb } from '../components/common/ContextualBreadcrumb'
import { ConvertInteractionToCaseModal } from '../components/interactions/ConvertInteractionToCaseModal'
import { ConvertInteractionToLegalProcessModal } from '../components/interactions/ConvertInteractionToLegalProcessModal'
import { getErrorMessage } from '../utils/error.utils'

type UpdateInteractionPayload = Partial<Omit<ClientInteraction, 'id' | 'clientId' | 'createdAt' | 'createdBy'>>

interface Interaction extends Omit<ClientInteraction, 'tags'> {
  tags: string[]
}

export function InteractionDetailsPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { formatDateTime } = useDateTimeFormat()

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [interaction, setInteraction] = useState<Interaction | null>(null)
  const [client, setClient] = useState<Client | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [taskRefreshTrigger, setTaskRefreshTrigger] = useState(0)

  // Conversion modals
  const [showConvertToCaseModal, setShowConvertToCaseModal] = useState(false)
  const [showConvertToLegalProcessModal, setShowConvertToLegalProcessModal] = useState(false)

  const [formData, setFormData] = useState({
    type: 'meeting' as InteractionType,
    status: 'scheduled' as InteractionStatus,
    priority: 'medium' as InteractionPriority,
    subject: '',
    description: '',
    outcome: '',
    scheduledFor: '',
    completedAt: '',
    tags: [] as string[]
  })

  useEffect(() => {
    if (id) {
      loadInteraction(id)
    }
  }, [id])

  const loadInteraction = async (interactionId: string) => {
    try {
      setLoading(true)
      setError(null)

      // Buscar todos os clientes e suas interações para encontrar a específica
      const clientsResponse = await clientService.getAllClients({}, { page: 1, limit: 1000 })

      let foundInteraction: Interaction | null = null
      let foundClient: Client | null = null

      for (const client of clientsResponse.clients) {
        const interactions = await clientService.getClientInteractions(client.id)
        const match = interactions.find((i) => (i as { id: string }).id === interactionId)

        if (match) {
          foundInteraction = match as Interaction
          foundClient = client
          break
        }
      }

      if (!foundInteraction || !foundClient) {
        throw new Error('Atendimento não encontrado')
      }

      setInteraction(foundInteraction)
      setClient(foundClient)
      setFormData({
        type: foundInteraction.type,
        status: foundInteraction.status,
        priority: foundInteraction.priority,
        subject: foundInteraction.subject,
        description: foundInteraction.description || '',
        outcome: foundInteraction.outcome || '',
        scheduledFor: foundInteraction.scheduledFor ? foundInteraction.scheduledFor.substring(0, 16) : '',
        completedAt: foundInteraction.completedAt ? foundInteraction.completedAt.substring(0, 16) : '',
        tags: foundInteraction.tags || []
      })
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao carregar atendimento'))
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (field: string, value: unknown) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleAddTag = (raw: string) => {
    const tag = raw.trim()
    if (tag && !formData.tags.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }))
    }
  }

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }))
  }

  const handleSave = async () => {
    if (!interaction || !client || !id) return

    setSaving(true)
    setError(null)

    try {
      const updateData: UpdateInteractionPayload = {
        type: formData.type,
        status: formData.status,
        priority: formData.priority,
        subject: formData.subject.trim(),
        tags: formData.tags,
        description: formData.description.trim() || undefined,
        outcome: formData.outcome.trim() || undefined,
        scheduledFor: formData.scheduledFor
          ? new Date(formData.scheduledFor).toISOString()
          : undefined,
        completedAt: formData.completedAt
          ? new Date(formData.completedAt).toISOString()
          : undefined
      }

      await clientService.updateInteraction(client.id, id, updateData)
      await loadInteraction(id)
      setIsEditing(false)
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao atualizar atendimento'))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!interaction || !client || !id) return

    if (window.confirm('Tem certeza que deseja excluir este atendimento?')) {
      try {
        await clientService.deleteInteraction(client.id, id)
        navigate(`/clients/${client.id}`)
      } catch (err: unknown) {
        setError(getErrorMessage(err, 'Erro ao excluir atendimento'))
      }
    }
  }

  // Conversion handlers
  const handleConvertToCaseSuccess = (newCase: { id: string }) => {
    navigate(`/cases/${newCase.id}`)
  }

  const handleConvertToLegalProcessSuccess = (newLegalProcess: { id: string }) => {
    navigate(`/cases/${newLegalProcess.id}`)
  }

  // Check if interaction is already converted
  const isConverted: boolean = !!interaction && (
    interaction.status === 'converted_to_case' ||
    interaction.status === 'converted_to_legal_process'
  )

  const editableStatusLabels = Object.fromEntries(
    Object.entries(statusLabels).filter(([status]) => !status.startsWith('converted_to_'))
  ) as Record<Exclude<InteractionStatus, 'converted_to_case' | 'converted_to_legal_process'>, string>

  if (loading) {
    return (
      <div className="flex flex-col gap-4 p-4 sm:p-6" aria-busy="true">
        <span className="sr-only">Carregando atendimento...</span>
        <Skeleton className="h-5 w-64" />
        <Skeleton className="h-28" />
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <Skeleton className="h-64 lg:col-span-2" />
          <Skeleton className="h-64" />
        </div>
      </div>
    )
  }

  if (error && !interaction) {
    return (
      <div className="flex flex-col gap-4 p-4 sm:p-6">
        <div role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 p-4">
          <h3 className="font-medium text-destructive">Erro ao carregar atendimento</h3>
          <p className="mt-1 text-sm text-muted-foreground">{error}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {id && <Button onClick={() => loadInteraction(id)}>Tentar novamente</Button>}
            <Button variant="outline" asChild><Link to="/interactions">Voltar para atendimentos</Link></Button>
          </div>
        </div>
      </div>
    )
  }

  if (!interaction || !client) {
    return (
      <div className="flex flex-col items-center gap-2 p-4 py-12 text-center sm:p-6">
        <MessageSquare className="size-10 text-muted-foreground" aria-hidden="true" />
        <p className="text-sm text-muted-foreground">Atendimento não encontrado</p>
        <Button asChild className="mt-4"><Link to="/interactions">Voltar para atendimentos</Link></Button>
      </div>
    )
  }

  const clientName = getClientDisplayName(client)

  return (
    <div className="flex flex-col gap-4 p-4 sm:p-6">
      <ContextualBreadcrumb
        items={[
          { label: 'Clientes', href: '/clients' },
          { label: clientName, href: `/clients/${client.id}` },
          { label: 'Atendimentos', href: `/clients/${client.id}`, icon: <MessageSquare className="size-4" /> },
          { label: interaction.subject || interactionTypeLabels[interaction.type] }
        ]}
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button variant="ghost" size="sm" asChild className="-ml-2">
          <Link to={`/clients/${client.id}`}><ArrowLeft className="size-4" /> Voltar</Link>
        </Button>
        <div className="flex flex-wrap items-center gap-2">
          {isEditing ? (
            <>
              <Button variant="outline" onClick={() => setIsEditing(false)}>
                <X className="size-4" /> Cancelar
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
                Salvar
              </Button>
            </>
          ) : (
            <>
              {/* Conversão: só enquanto o atendimento não foi convertido */}
              {!isConverted && (
                <>
                  <Button variant="outline" onClick={() => setShowConvertToCaseModal(true)} title="Converter este atendimento em um caso">
                    <Briefcase className="size-4" /> Converter em Caso
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowConvertToLegalProcessModal(true)}
                    title="Converter este atendimento diretamente em processo judicial"
                  >
                    <Scale className="size-4" /> Converter em Processo
                  </Button>
                </>
              )}
              <Button
                variant="outline"
                onClick={() => setIsEditing(true)}
                disabled={isConverted}
                title={isConverted ? 'Atendimento já convertido - não pode ser editado' : 'Editar atendimento'}
              >
                <Edit className="size-4" /> Editar
              </Button>
              <Button
                variant="outline"
                onClick={handleDelete}
                disabled={isConverted}
                className="text-destructive hover:text-destructive"
                title={isConverted ? 'Atendimento já convertido - não pode ser excluído' : 'Excluir atendimento'}
              >
                <Trash2 className="size-4" /> Excluir
              </Button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      <Tabs defaultValue="details" className="w-full gap-4">
        <Card className="gap-0 pb-0">
          <CardContent className="flex flex-col gap-3 pb-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <MessageSquare className="size-6 shrink-0 text-muted-foreground" aria-hidden="true" />
                <h1 className="min-w-0 break-words text-2xl font-semibold">Detalhes do Atendimento</h1>
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
                <span className="tabular-nums">#{interaction.id.substring(0, 8)}</span>
                <span className="hidden sm:inline" aria-hidden="true">•</span>
                <span>{interactionTypeLabels[interaction.type]}</span>
              </div>
            </div>
            <div className="flex flex-row items-center gap-2 sm:flex-col sm:items-end">
              <InteractionStatusBadge status={interaction.status} />
              <InteractionPriorityText priority={interaction.priority} className="text-sm" />
            </div>
          </CardContent>
          <div className="overflow-x-auto border-t px-2">
            <TabsList variant="line" className="h-auto justify-start">
              <TabsTrigger value="details" className="px-3 py-3">Detalhes do Atendimento</TabsTrigger>
              <TabsTrigger value="tasks" className="px-3 py-3">Tarefas</TabsTrigger>
            </TabsList>
          </div>
        </Card>

        <TabsContent value="details">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="flex min-w-0 flex-col gap-4 lg:col-span-2">
              <DetailSection title="Informações do Atendimento" icon={FileText}>
                {isEditing ? (
                  <div className="grid grid-cols-1 gap-4">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                      <FormField label="Tipo" htmlFor="interaction-type">
                        <NativeSelect
                          id="interaction-type"
                          value={formData.type}
                          onChange={(e) => handleInputChange('type', e.target.value as InteractionType)}
                        >
                          {Object.entries(interactionTypeLabels).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </NativeSelect>
                      </FormField>
                      <FormField label="Status" htmlFor="interaction-status">
                        <NativeSelect
                          id="interaction-status"
                          value={formData.status}
                          onChange={(e) => handleInputChange('status', e.target.value as InteractionStatus)}
                        >
                          {Object.entries(editableStatusLabels).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </NativeSelect>
                      </FormField>
                      <FormField label="Prioridade" htmlFor="interaction-priority">
                        <NativeSelect
                          id="interaction-priority"
                          value={formData.priority}
                          onChange={(e) => handleInputChange('priority', e.target.value as InteractionPriority)}
                        >
                          {Object.entries(priorityLabels).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </NativeSelect>
                      </FormField>
                    </div>

                    <FormField label="Assunto" htmlFor="interaction-subject">
                      <Input
                        id="interaction-subject"
                        type="text"
                        value={formData.subject}
                        onChange={(e) => handleInputChange('subject', e.target.value)}
                      />
                    </FormField>

                    <FormField label="Descrição" htmlFor="interaction-description">
                      <Textarea
                        id="interaction-description"
                        rows={4}
                        value={formData.description}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                      />
                    </FormField>

                    <FormField label="Resultado/Desfecho" htmlFor="interaction-outcome">
                      <Textarea
                        id="interaction-outcome"
                        rows={3}
                        value={formData.outcome}
                        onChange={(e) => handleInputChange('outcome', e.target.value)}
                      />
                    </FormField>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <FormField label="Data/Hora Agendada" htmlFor="interaction-scheduled-for">
                        <Input
                          id="interaction-scheduled-for"
                          type="datetime-local"
                          value={formData.scheduledFor}
                          onChange={(e) => handleInputChange('scheduledFor', e.target.value)}
                        />
                      </FormField>
                      {(formData.status === 'completed' || interaction.completedAt) && (
                        <FormField label="Data/Hora de Conclusão" htmlFor="interaction-completed-at">
                          <Input
                            id="interaction-completed-at"
                            type="datetime-local"
                            value={formData.completedAt}
                            onChange={(e) => handleInputChange('completedAt', e.target.value)}
                          />
                        </FormField>
                      )}
                    </div>

                    <FormField label="Tags" htmlFor="interaction-tags">
                      <TagsInput
                        id="interaction-tags"
                        tags={formData.tags}
                        onAdd={handleAddTag}
                        onRemove={handleRemoveTag}
                      />
                    </FormField>
                  </div>
                ) : (
                  <dl className="grid grid-cols-1 gap-4">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                      <DetailField label="Tipo">{interactionTypeLabels[interaction.type]}</DetailField>
                      <DetailField label="Status"><InteractionStatusBadge status={interaction.status} /></DetailField>
                      <DetailField label="Prioridade"><InteractionPriorityText priority={interaction.priority} className="text-sm" /></DetailField>
                    </div>
                    <DetailField label="Assunto">{interaction.subject}</DetailField>
                    <DetailField label="Descrição">
                      <span className="whitespace-pre-wrap">{interaction.description || '-'}</span>
                    </DetailField>
                    <DetailField label="Resultado/Desfecho">
                      <span className="whitespace-pre-wrap">{interaction.outcome || '-'}</span>
                    </DetailField>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <DetailField label="Data/Hora Agendada">
                        {interaction.scheduledFor ? formatDateTime(interaction.scheduledFor) : '-'}
                      </DetailField>
                      {interaction.completedAt && (
                        <DetailField label="Data/Hora de Conclusão">{formatDateTime(interaction.completedAt)}</DetailField>
                      )}
                    </div>
                  </dl>
                )}
              </DetailSection>

              {!isEditing && (
                <DetailSection title="Tags" icon={Tag}>
                  {interaction.tags && interaction.tags.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {interaction.tags.map((tag, index) => <Badge key={index} variant="secondary">{tag}</Badge>)}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">Nenhuma tag</p>
                  )}
                </DetailSection>
              )}
            </div>

            <div className="flex min-w-0 flex-col gap-4">
              <DetailSection title="Contato" icon={User}>
                <dl className="flex flex-col gap-3">
                  <DetailField label="Nome">
                    <Link to={`/clients/${client.id}`} className="text-primary underline-offset-4 hover:underline">
                      {clientName}
                    </Link>
                  </DetailField>
                  <DetailField label="Número">#{client.clientNumber}</DetailField>
                </dl>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  {client.whatsappPhone && (
                    <WhatsAppLink
                      phone={client.whatsappPhone}
                      label="WhatsApp"
                      message={`Olá ${clientName}, tudo bem?`}
                    />
                  )}
                  <Button variant="outline" size="sm" asChild>
                    <Link to={`/clients/${client.id}`}><ExternalLink className="size-4" /> Ver Perfil</Link>
                  </Button>
                </div>
              </DetailSection>

              <DetailSection title="Metadados" icon={History}>
                <dl className="flex flex-col gap-3">
                  <DetailField label="Criado em">{formatDateTime(interaction.createdAt)}</DetailField>
                  <DetailField label="Última atualização">{formatDateTime(interaction.updatedAt)}</DetailField>
                </dl>
              </DetailSection>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="tasks">
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-lg font-semibold">Tarefas deste Atendimento</h2>
              <TaskQuickCreateButton
                context={{
                  clientId: client.id,
                  clientName,
                  interactionId: interaction.id,
                  interactionSubject: interaction.subject,
                  originType: 'from_interaction'
                } as TaskContext}
                onTaskCreated={() => setTaskRefreshTrigger(prev => prev + 1)}
              />
            </div>
            <TaskListByEntity
              entityType="interaction"
              entityId={interaction.id}
              showCase={true}
              refreshTrigger={taskRefreshTrigger}
            />
          </div>
        </TabsContent>
      </Tabs>

      <ConvertInteractionToCaseModal
        isOpen={showConvertToCaseModal}
        onClose={() => setShowConvertToCaseModal(false)}
        onSuccess={handleConvertToCaseSuccess}
        interaction={{
          id: interaction.id,
          subject: interaction.subject,
          description: interaction.description
        }}
      />

      <ConvertInteractionToLegalProcessModal
        isOpen={showConvertToLegalProcessModal}
        onClose={() => setShowConvertToLegalProcessModal(false)}
        onSuccess={handleConvertToLegalProcessSuccess}
        interaction={{
          id: interaction.id,
          subject: interaction.subject,
          description: interaction.description
        }}
      />
    </div>
  )
}
